import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getAuth, deleteUser, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { getDatabase, ref, get, update, query, orderByChild, equalTo } from 'firebase/database';

/** Deletes everything that's only yours, then the login itself (same list as DataHandler.swift on iOS). */
async function performDeleteAccount() {
  const user = getAuth().currentUser;
  if (!user) return;
  const uid = user.uid;
  const db = getDatabase();

  const nodesToClean = [
    { path: 'rideReserve',    field: 'userID' },
    { path: 'rideRequest',    field: 'userID' },
    { path: 'cars',           field: 'userID' },
    { path: 'bookings',       field: 'bookerUserID' },
    { path: 'bookings',       field: 'providerUserID' },
    { path: 'rentalBookings', field: 'renterUserID' },
    { path: 'rentalBookings', field: 'ownerUserID' },
    { path: 'activityFeed',   field: 'userId' },
  ];

  const updates = { [`users/${uid}`]: null };
  await Promise.all(nodesToClean.map(async ({ path, field }) => {
    const snap = await get(query(ref(db, path), orderByChild(field), equalTo(uid)));
    snap.forEach(child => { updates[`${path}/${child.key}`] = null; });
  }));

  // Single atomic multi-path delete — clears all data while still authenticated.
  await update(ref(db), updates);

  // Private contact entry, booking links, ride alerts and block list — separate
  // so a rules problem here can never block account deletion.
  await update(ref(db), {
    [`contacts/${uid}`]: null, [`connections/${uid}`]: null,
    [`rideAlerts/${uid}`]: null, [`blocks/${uid}`]: null,
  }).catch(() => {});

  // Data gone — now delete the login.
  await deleteUser(user);
}

/**
 * Settings → Delete account. Returns `start` (asks twice, then deletes) and
 * the password prompt Firebase needs when the last sign-in was a while ago.
 */
export function useDeleteAccount() {
  const navigate = useNavigate();
  const [askPassword, setAskPassword] = useState(false);
  const [password,    setPassword]    = useState('');
  const [error,       setError]       = useState('');
  const [busy,        setBusy]        = useState(false);

  const start = async () => {
    if (!window.confirm('Delete your account? This will permanently remove your profile, all posted rides, car listings, and bookings.')) return;
    if (!window.confirm('This cannot be undone. Are you absolutely sure?')) return;
    try {
      await performDeleteAccount();
      navigate('/');
    } catch (e) {
      if (e.code === 'auth/requires-recent-login') setAskPassword(true);
      else alert('Failed to delete account: ' + e.message);
    }
  };

  const close = () => { setAskPassword(false); setPassword(''); setError(''); };

  const confirmWithPassword = async () => {
    const user = getAuth().currentUser;
    if (!user || !password) return;
    setBusy(true); setError('');
    try {
      await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
      await performDeleteAccount();
      close();
      navigate('/');
    } catch (e) {
      setError(e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential'
        ? 'Incorrect password. Please try again.' : e.message);
    }
    setBusy(false);
  };

  const modal = askPassword && ReactDOM.createPortal(
    <div className="safety-overlay" onClick={close}>
      <div className="safety-dialog" role="dialog" aria-modal="true" aria-labelledby="reauth-title"
        onClick={e => e.stopPropagation()}>
        <h2 id="reauth-title">Confirm it's you</h2>
        <p className="report-hint">For your security, re-enter your password to delete your account.</p>
        <input className="report-note" type="password" placeholder="Password" autoFocus
          value={password} onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && confirmWithPassword()} />
        {error && <p className="report-error">{error}</p>}
        <div className="safety-actions">
          <button type="button" className="safety-secondary" onClick={close}>Cancel</button>
          <button type="button" className="safety-primary" onClick={confirmWithPassword} disabled={busy || !password}>
            {busy ? 'Deleting…' : 'Delete account'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

  return { start, modal };
}
